import type { ReactNode } from 'react';
import { FileText, ShieldCheck, TrendingUp } from 'lucide-react';
import { BrandLockup, Wordmark } from '../components/Brand';
import { APP_NAME } from '../lib/navigation';

const HIGHLIGHTS = [
  { icon: TrendingUp, text: 'IDC, IQA, ISQ e IQW com a memória de cálculo aberta' },
  { icon: FileText, text: 'RNCs, certificados e documentos num só lugar' },
  { icon: ShieldCheck, text: 'Histórico preservado para auditoria IATF 16949' },
] as const;

/**
 * Moldura das telas públicas (login, esqueci a senha, definir senha).
 *
 * Painel da marca à esquerda no desktop; no celular ele some e sobra só o
 * formulário — o fornecedor costuma abrir o portal pelo telefone.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col lg:flex-row">
      <aside className="relative hidden overflow-hidden bg-brand-800 lg:flex lg:w-[46%] lg:flex-col lg:justify-between lg:p-14">
        {/* Profundidade: um brilho quente da cor de destaque e um frio da marca,
            bem difusos, para o azul chapado não ficar plano. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 -top-40 size-[28rem] rounded-full bg-accent-400/15 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 -left-28 size-[26rem] rounded-full bg-brand-400/25 blur-3xl"
        />
        {/* Trama de pontos discreta, quase imperceptível, que dá textura. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage: 'radial-gradient(circle, rgb(255 255 255 / 0.35) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />

        <BrandLockup tone="light" size="lg" className="relative self-start" />

        <div className="relative max-w-md">
          <h2 className="text-[2rem] font-semibold leading-[1.15] tracking-tight text-white">
            {APP_NAME}
          </h2>
          <p className="mt-4 text-[0.95rem] leading-relaxed text-brand-100">
            Acompanhe mês a mês seu desempenho de entrega, qualidade e certificação junto à
            Metalúrgica Weloze.
          </p>

          <ul className="mt-11 space-y-3">
            {HIGHLIGHTS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3.5">
                {/* Caixa de tamanho fixo e conteúdo centrado: o alinhamento não
                    depende do padding nem da altura da linha do texto. */}
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-inset ring-white/15">
                  <Icon className="size-[1.05rem] text-accent-300" strokeWidth={1.75} aria-hidden />
                </span>
                <span className="text-sm leading-snug text-brand-50">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs leading-relaxed text-brand-200/70">
          Metalúrgica Weloze Ltda · Sistema da Qualidade para Fornecedores
        </p>
      </aside>

      <main className="flex flex-1 items-center justify-center px-5 py-12 sm:px-8">
        <div className="w-full max-w-sm animate-rise">
          {/* No celular o painel da esquerda nao existe, entao a identificacao
              da aplicacao precisa aparecer aqui. */}
          <div className="mb-9 lg:hidden">
            <Wordmark size="lg" />
            <p className="mt-1 text-sm font-medium text-slate-500">{APP_NAME}</p>
          </div>

          <h1 className="text-[1.625rem] font-semibold leading-tight tracking-tight text-slate-900">
            {title}
          </h1>
          {subtitle && <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{subtitle}</p>}

          <div className="mt-8">{children}</div>
        </div>
      </main>
    </div>
  );
}
